import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '@nanostores/react';
import { Button, Input, Textarea, cn } from '@ideoz/ui';
import type { ComputedStylePayload } from '../../preview-bridge/protocol';
import type { HotEditPropSection } from '../../preview-bridge/elementKind';
import { hotEditStore } from '../../stores/hotEdit';
import { BoxMetricRow, FieldRow, SectionCard, SectionTitle, UnitInputRow, selectCls } from './primitives';

const SEMANTIC_FILLS: { label: string; util: string; approxCss: string }[] = [
  { label: 'Primary', util: 'bg-primary', approxCss: 'hsl(222.2deg 47.4% 11.2%)' },
  { label: 'Secondary', util: 'bg-secondary', approxCss: 'hsl(210deg 40% 96%)' },
  { label: 'Muted', util: 'bg-muted', approxCss: 'hsl(210deg 40% 96%)' },
  { label: 'Accent', util: 'bg-accent', approxCss: 'hsl(210deg 40% 96%)' },
  { label: 'Card', util: 'bg-card', approxCss: '#ffffff' },
  { label: 'Background', util: 'bg-background', approxCss: '#ffffff' },
  { label: 'Destructive', util: 'bg-destructive', approxCss: '#ef4444' },
];

const SEMANTIC_TEXT: { label: string; util: string; approxCss: string }[] = [
  { label: 'Primary fg', util: 'text-primary-foreground', approxCss: 'hsl(210deg 40% 98%)' },
  { label: 'Foreground', util: 'text-foreground', approxCss: 'hsl(222.2deg 84% 4.9%)' },
  { label: 'Muted fg', util: 'text-muted-foreground', approxCss: '#64748b' },
];

function stripPx(raw: string | undefined): string {
  if (!raw) return '';
  const s = raw.trim();
  const m = /^([\d.]+)px$/i.exec(s);
  if (m) return m[1];
  return s.replace(/\s*px\s*$/i, '').trim();
}

function rgbToHexApprox(raw: string): string {
  const rgb = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(raw.trim());
  if (!rgb) return raw.startsWith('#') ? raw.slice(0, 7) : raw;
  const h = (n: string) => Number(n).toString(16).padStart(2, '0');
  return `#${h(rgb[1])}${h(rgb[2])}${h(rgb[3])}`;
}

const HotEditInner = React.memo(function HotEditInnerMemo({ selectionId }: { selectionId: string }) {
  const selection = useStore(hotEditStore.selection);
  const api = useStore(hotEditStore.bridgeApi);
  type Bridge = NonNullable<typeof api>;

  if (!selection || selection.elementId !== selectionId) return null;
  if (!api) {
    return <p className="text-[11px] text-muted-foreground px-3 py-4">Connecting to preview…</p>;
  }

  const sections = selection.editableSections ?? [];
  const has = (s: HotEditPropSection) => sections.includes(s);
  const isBlocked = !!selection.blockReason || selection.elementKind === 'unsupported';
  const canPersist = selection.canPersistClasses !== false && !!selection.source?.filePath;

  const bump = api.bumpMergedFromTailwind;
  const persist = api.queuePersistTypographyLayout;

  const pushCss = (prop: string, value: string, bridge: Bridge) => {
    bridge.applyInstantCss(selection!.elementId, prop, value);
    bump(prop, value);
    persist();
  };

  const pushSemanticBg = (util: string, approx: string, bridge: Bridge) => {
    bridge.applyInstantCss(selection!.elementId, 'backgroundColor', approx);
    bump('__hot_tw_util', util);
    persist();
  };

  const pushSemanticText = (util: string, approx: string, bridge: Bridge) => {
    bridge.applyInstantCss(selection!.elementId, 'color', approx);
    bump('__hot_tw_util', util);
    persist();
  };

  const computed = selection.computedStyles as ComputedStylePayload;

  const [fontSize, setFontSize] = useState(() => stripPx(computed.fontSize));
  const [fontWeight, setFontWeight] = useState(() => String(computed.fontWeight ?? '400'));
  const [colorHex, setColorHex] = useState(() => rgbToHexApprox(String(computed.color ?? '#000000')));
  const [textAlign, setTextAlign] = useState(() => String(computed.textAlign ?? 'start'));
  const [lineHeight, setLineHeight] = useState(() => String(computed.lineHeight ?? ''));
  const [letterSpacing, setLetterSpacing] = useState(() => stripPx(computed.letterSpacing));
  const [fontFamilyInput, setFontFamilyInput] = useState(() =>
    String(computed.fontFamily ?? 'system-ui').split(',')[0].trim().replace(/["']/g, ''),
  );
  const [widthPx, setWidthPx] = useState(() => stripPx(computed.width));
  const [heightPx, setHeightPx] = useState(() => stripPx(computed.height));
  const [opacity, setOpacity] = useState(() => String(computed.opacity ?? '1'));

  const [paddingT, setPaddingT] = useState(() => stripPx(computed.paddingTop));
  const [paddingR, setPaddingR] = useState(() => stripPx(computed.paddingRight));
  const [paddingB, setPaddingB] = useState(() => stripPx(computed.paddingBottom));
  const [paddingL, setPaddingL] = useState(() => stripPx(computed.paddingLeft));
  const [marginT, setMarginT] = useState(() => stripPx(computed.marginTop));
  const [marginR, setMarginR] = useState(() => stripPx(computed.marginRight));
  const [marginB, setMarginB] = useState(() => stripPx(computed.marginBottom));
  const [marginL, setMarginL] = useState(() => stripPx(computed.marginLeft));

  const [gapPx, setGapPx] = useState(() => stripPx(computed.gap));
  const [flexDir, setFlexDir] = useState(() => String(computed.flexDirection ?? 'row'));
  const [justify, setJustify] = useState(() => String(computed.justifyContent ?? 'flex-start'));
  const [alignItemsSt, setAlignItemsSt] = useState(() => String(computed.alignItems ?? 'stretch'));
  const [display, setDisplay] = useState(() => String(computed.display ?? 'block'));

  const [fillHex, setFillHex] = useState(() =>
    rgbToHexApprox(String(computed.backgroundColor ?? computed.fill ?? '#ffffff')),
  );

  const [borderTw, setBorderTw] = useState(() => stripPx(computed.borderTopWidth));
  const [borderRw, setBorderRw] = useState(() => stripPx(computed.borderRightWidth));
  const [borderBw, setBorderBw] = useState(() => stripPx(computed.borderBottomWidth));
  const [borderLw, setBorderLw] = useState(() => stripPx(computed.borderLeftWidth));
  const [borderStyleState, setBorderStyleState] = useState(() => String(computed.borderStyle ?? 'solid'));
  const [borderColor, setBorderColor] = useState(() => rgbToHexApprox(String(computed.borderTopColor ?? '#000000')));
  const [radius, setRadius] = useState(() => stripPx(computed.borderRadius));

  const [objectFitSel, setObjectFitSel] = useState(() => String(computed.objectFit ?? 'cover'));
  const [svgFillHex, setSvgFillHex] = useState(() => rgbToHexApprox(String(computed.fill ?? '#000000')));
  const [svgStrokeHex, setSvgStrokeHex] = useState(() => rgbToHexApprox(String(computed.stroke ?? '#000000')));

  const [textDraft, setTextDraft] = useState(() => selection.textContent ?? '');
  const [placeholderDraft, setPlaceholderDraft] = useState(() => selection.inputPlaceholder ?? '');
  const [valueDraft, setValueDraft] = useState(() => selection.inputValue ?? '');
  const [inputTypeDraft, setInputTypeDraft] = useState(() => selection.inputType ?? 'text');
  const [optionsDraft, setOptionsDraft] = useState(() =>
    (selection.optionValues ?? []).join('\n'),
  );

  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const valueInputRef = useRef<HTMLInputElement>(null);
  const placeholderInputRef = useRef<HTMLInputElement>(null);

  const applyBoxShorthand = (
    prefix: 'padding' | 'margin',
    px: string,
    bridge: Bridge,
    setters: {
      setT: (v: string) => void;
      setR: (v: string) => void;
      setB: (v: string) => void;
      setL: (v: string) => void;
    },
  ) => {
    const v = `${px}px`;
    setters.setT(px);
    setters.setR(px);
    setters.setB(px);
    setters.setL(px);
    pushCss(`${prefix}Top`, v, bridge);
    pushCss(`${prefix}Right`, v, bridge);
    pushCss(`${prefix}Bottom`, v, bridge);
    pushCss(`${prefix}Left`, v, bridge);
  };

  useEffect(() => {
    setFontSize(stripPx(computed.fontSize));
    setFontWeight(String(computed.fontWeight ?? '400'));
    setColorHex(rgbToHexApprox(String(computed.color ?? '#000000')));
    setTextAlign(String(computed.textAlign ?? 'start'));
    setLineHeight(String(computed.lineHeight ?? ''));
    setLetterSpacing(stripPx(computed.letterSpacing));
    setFontFamilyInput(String(computed.fontFamily ?? 'system-ui').split(',')[0].trim().replace(/["']/g, ''));
    setWidthPx(stripPx(computed.width));
    setHeightPx(stripPx(computed.height));
    setOpacity(String(computed.opacity ?? '1'));
    setPaddingT(stripPx(computed.paddingTop));
    setPaddingR(stripPx(computed.paddingRight));
    setPaddingB(stripPx(computed.paddingBottom));
    setPaddingL(stripPx(computed.paddingLeft));
    setMarginT(stripPx(computed.marginTop));
    setMarginR(stripPx(computed.marginRight));
    setMarginB(stripPx(computed.marginBottom));
    setMarginL(stripPx(computed.marginLeft));
    setGapPx(stripPx(computed.gap));
    setFlexDir(String(computed.flexDirection ?? 'row'));
    setJustify(String(computed.justifyContent ?? 'flex-start'));
    setAlignItemsSt(String(computed.alignItems ?? 'stretch'));
    setDisplay(String(computed.display ?? 'block'));
    setFillHex(rgbToHexApprox(String(computed.backgroundColor ?? computed.fill ?? '#ffffff')));
    setBorderTw(stripPx(computed.borderTopWidth));
    setBorderRw(stripPx(computed.borderRightWidth));
    setBorderBw(stripPx(computed.borderBottomWidth));
    setBorderLw(stripPx(computed.borderLeftWidth));
    setBorderStyleState(String(computed.borderStyle ?? 'solid'));
    setBorderColor(rgbToHexApprox(String(computed.borderTopColor ?? '#000000')));
    setRadius(stripPx(computed.borderRadius));
    setObjectFitSel(String(computed.objectFit ?? 'cover'));
    setSvgFillHex(rgbToHexApprox(String(computed.fill ?? '#000000')));
    setSvgStrokeHex(rgbToHexApprox(String(computed.stroke ?? '#000000')));
    setInputTypeDraft(selection.inputType ?? 'text');
    setOptionsDraft((selection.optionValues ?? []).join('\n'));
  }, [selectionId]);

  useEffect(() => {
    if (document.activeElement === textAreaRef.current) return;
    setTextDraft(selection.textContent ?? '');
  }, [selectionId, selection.textContent]);

  useEffect(() => {
    if (document.activeElement === valueInputRef.current) return;
    setValueDraft(selection.inputValue ?? '');
  }, [selectionId, selection.inputValue]);

  useEffect(() => {
    if (document.activeElement === placeholderInputRef.current) return;
    setPlaceholderDraft(selection.inputPlaceholder ?? '');
  }, [selectionId, selection.inputPlaceholder]);

  const sourceHint = useMemo(() => {
    if (!selection?.source?.filePath) return null;
    return `${selection.source.filePath}:${selection.source.lineNumber}`;
  }, [selection]);

  const hasListBinding = selection?.listContext?.inMapLoop === true;

  const mappedListHint = useMemo(() => {
    const lc = selection?.listContext;
    if (!lc?.inMapLoop) return null;
    const styleNote = ' Layout and typography changes apply to the shared row template (every list item).';
    if (lc.reactKey != null && lc.reactKey !== '')
      return `List row key “${lc.reactKey}”: text saves to that item’s data (same file or imported mock under src/).${styleNote}`;
    if (lc.mapRowIndex != null && lc.mapRowIndex > 0)
      return `List row ${lc.mapRowIndex}: text saves to the matching array entry.${styleNote}`;
    return `Rendered from a list: text saves to source data.${styleNote}`;
  }, [selection]);

  if (isBlocked) {
    return (
      <div className="px-3 py-4 space-y-2">
        <p className="text-xs font-semibold text-foreground">Hot edit unavailable</p>
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          {selection.blockReason ?? 'This selection cannot be tweaked from the inspector.'}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-0 h-full">
        <div className="sticky top-0 z-10 border-b border-border/60 bg-card/95 backdrop-blur px-3 py-2 flex items-center justify-between gap-2 shrink-0">
        <div className="min-w-0 space-y-0.5">
          <div className="text-xs font-semibold truncate capitalize">{selection.tagName}</div>
          <div className="text-[10px] text-muted-foreground font-mono truncate" title={sourceHint ?? ''}>
            {sourceHint ?? 'inline preview only'}
          </div>
          {mappedListHint ? (
            <p className="text-[10px] text-muted-foreground leading-snug">{mappedListHint}</p>
          ) : selection.tagName === 'tr' ||
            selection.tagName === 'td' ||
            selection.tagName === 'th' ? (
            <p className="text-[10px] text-muted-foreground leading-snug">
              Table markup from a `.map()` uses one shared template — class tweaks typically apply to every row.
            </p>
          ) : null}
        </div>
        {sourceHint && canPersist ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-[11px] shrink-0"
            onClick={() => api.openSelectionInEditor()}
          >
            Code
          </Button>
        ) : null}
      </div>

      {!canPersist && selection.blockReason == null ? (
        <p className="text-[11px] text-amber-800 bg-amber-50 border-b border-amber-100 px-3 py-2">
          Saving to TSX is limited for this hit — inline preview still works.
        </p>
      ) : null}

      <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-3 text-sm">
        {has('content') ? (
          <SectionCard title="Content">
            <FieldRow label={hasListBinding ? 'Row text' : 'Text'}>
              <Textarea
                ref={textAreaRef}
                value={textDraft}
                disabled={selection.hasChildElements && !hasListBinding}
                title={
                  selection.hasChildElements && !hasListBinding
                    ? 'Nested markup — edit copy in Code view.'
                    : hasListBinding
                      ? 'Updates the matching item in the source data array (same file or imported).'
                      : 'Updates JSX text when source mapping exists.'
                }
                className="text-xs min-h-[72px]"
                onChange={(e) => {
                  const v = e.target.value;
                  setTextDraft(v);
                  /** Skip live preview when nested children exist; replacing textContent would wipe icons. */
                  if (!selection.hasChildElements) {
                    api.scheduleApplyInstantText(selection.elementId, v);
                  }
                }}
                onBlur={() => api.queuePersistText(textDraft)}
              />
            </FieldRow>
          </SectionCard>
        ) : null}

        {has('form') ? (
          <SectionCard title="Form">
            {selection.tagName === 'select' ? (
              <FieldRow label="Options">
                <Textarea
                  className="text-xs min-h-[96px] font-mono"
                  value={optionsDraft}
                  placeholder="One option per line"
                  onChange={(e) => setOptionsDraft(e.target.value)}
                  onBlur={() => {
                    const lines = optionsDraft
                      .split('\n')
                      .map((l) => l.trim())
                      .filter(Boolean);
                    api.queuePersistSelectOptions(lines);
                  }}
                />
              </FieldRow>
            ) : selection.tagName === 'textarea' ? (
              <>
                <FieldRow label="Value">
                  <Textarea
                    ref={textAreaRef as React.RefObject<HTMLTextAreaElement>}
                    className="text-xs min-h-[72px]"
                    value={valueDraft}
                    onChange={(e) => {
                      const v = e.target.value;
                      setValueDraft(v);
                      api.applyInstantDom(selection.elementId, 'value', v);
                    }}
                    onBlur={() => api.queuePersistDomValue(valueDraft)}
                  />
                </FieldRow>
                <FieldRow label="Placeholder">
                  <Input
                    ref={placeholderInputRef}
                    className="h-8 text-xs"
                    value={placeholderDraft}
                    onChange={(e) => {
                      const v = e.target.value;
                      setPlaceholderDraft(v);
                      api.applyInstantDom(selection.elementId, 'placeholder', v);
                    }}
                    onBlur={() => api.queuePersistPlaceholder(placeholderDraft)}
                  />
                </FieldRow>
              </>
            ) : (
              <>
                <FieldRow label="Value">
                  <Input
                    ref={valueInputRef}
                    className="h-8 text-xs"
                    value={valueDraft}
                    onChange={(e) => {
                      const v = e.target.value;
                      setValueDraft(v);
                      api.applyInstantDom(selection.elementId, 'value', v);
                    }}
                    onBlur={() => api.queuePersistDomValue(valueDraft)}
                  />
                </FieldRow>
                <FieldRow label="Placeholder">
                  <Input
                    ref={placeholderInputRef}
                    className="h-8 text-xs"
                    value={placeholderDraft}
                    onChange={(e) => {
                      const v = e.target.value;
                      setPlaceholderDraft(v);
                      api.applyInstantDom(selection.elementId, 'placeholder', v);
                    }}
                    onBlur={() => api.queuePersistPlaceholder(placeholderDraft)}
                  />
                </FieldRow>
                <FieldRow label="Type">
                  <select
                    className={selectCls}
                    value={inputTypeDraft}
                    onChange={(e) => {
                      const v = e.target.value;
                      setInputTypeDraft(v);
                      api.applyInstantDom(selection.elementId, 'type', v);
                      api.queuePersistInputType(v);
                    }}
                  >
                    {['text', 'email', 'password', 'number', 'search', 'tel', 'url'].map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </FieldRow>
              </>
            )}
          </SectionCard>
        ) : null}

        {has('typography') ? (
          <SectionCard title="Typography">
            <FieldRow label="Font stack">
              <Input
                className="h-8 text-xs"
                value={fontFamilyInput}
                onChange={(e) => setFontFamilyInput(e.target.value)}
                onBlur={() => pushCss('fontFamily', fontFamilyInput, api)}
              />
            </FieldRow>
            <div className="grid grid-cols-2 gap-2">
              <FieldRow label="Size (px)">
                <Input
                  className="h-8 text-xs"
                  value={fontSize}
                  onChange={(e) => setFontSize(e.target.value)}
                  onBlur={() => pushCss('fontSize', `${fontSize}px`, api)}
                />
              </FieldRow>
              <FieldRow label="Weight">
                <select
                  className={selectCls}
                  value={fontWeight}
                  title="Font weight"
                  onChange={(e) => {
                    const v = e.target.value;
                    setFontWeight(v);
                    pushCss('fontWeight', v, api);
                  }}
                >
                  {['300', '400', '500', '600', '700', '800'].map((w) => (
                    <option key={w} value={w}>
                      {w}
                    </option>
                  ))}
                </select>
              </FieldRow>
            </div>
            <FieldRow label="Color">
              <div className="space-y-1.5">
                <select
                  key={`${selectionId}-semantic-text`}
                  className={selectCls}
                  title="Semantic text colors"
                  defaultValue=""
                  onChange={(e) => {
                    const opt = SEMANTIC_TEXT.find((x) => x.label === e.target.value);
                    if (!opt) return;
                    setColorHex(rgbToHexApprox(opt.approxCss));
                    pushSemanticText(opt.util, opt.approxCss, api);
                  }}
                >
                  <option value="" disabled>
                    Token text…
                  </option>
                  {SEMANTIC_TEXT.map((o) => (
                    <option key={o.util} value={o.label}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    aria-label="Text color"
                    className="h-8 w-10 rounded border border-border bg-background cursor-pointer"
                    value={colorHex.length === 7 ? colorHex : '#000000'}
                    onChange={(e) => {
                      const v = e.target.value;
                      setColorHex(v);
                      pushCss('color', v, api);
                    }}
                  />
                  <Input
                    className="h-8 text-xs flex-1 font-mono"
                    value={colorHex}
                    onChange={(e) => setColorHex(e.target.value)}
                    onBlur={() => pushCss('color', colorHex, api)}
                  />
                </div>
              </div>
            </FieldRow>
            <div className="grid grid-cols-2 gap-2">
              <FieldRow label="Align">
                <select
                  className={selectCls}
                  value={textAlign}
                  title="Text align"
                  onChange={(e) => {
                    const v = e.target.value;
                    setTextAlign(v);
                    pushCss('textAlign', v, api);
                  }}
                >
                  <option value="start">start</option>
                  <option value="center">center</option>
                  <option value="end">end</option>
                  <option value="justify">justify</option>
                </select>
              </FieldRow>
              <FieldRow label="Line height">
                <Input
                  className="h-8 text-xs"
                  value={lineHeight}
                  onChange={(e) => setLineHeight(e.target.value)}
                  onBlur={() => pushCss('lineHeight', lineHeight, api)}
                />
              </FieldRow>
            </div>
            <FieldRow label="Tracking (px)">
              <Input
                className="h-8 text-xs"
                value={letterSpacing}
                onChange={(e) => setLetterSpacing(e.target.value)}
                onBlur={() => pushCss('letterSpacing', `${letterSpacing}px`, api)}
              />
            </FieldRow>
          </SectionCard>
        ) : null}

        {has('size') ? (
          <SectionCard title="Size">
            <div className="grid grid-cols-2 gap-2">
              <UnitInputRow label="Width" unit="px" value={widthPx} onChange={setWidthPx} onBlur={() => pushCss('width', `${widthPx}px`, api)} />
              <UnitInputRow label="Height" unit="px" value={heightPx} onChange={setHeightPx} onBlur={() => pushCss('height', `${heightPx}px`, api)} />
            </div>
            {selection.tagName === 'img' ? (
              <FieldRow label="Object fit">
                <select
                  className={selectCls}
                  value={objectFitSel}
                  onChange={(e) => {
                    const v = e.target.value;
                    setObjectFitSel(v);
                    pushCss('objectFit', v, api);
                  }}
                >
                  <option value="cover">cover</option>
                  <option value="contain">contain</option>
                  <option value="fill">fill</option>
                  <option value="none">none</option>
                  <option value="scale-down">scale-down</option>
                </select>
              </FieldRow>
            ) : null}
          </SectionCard>
        ) : null}

        {has('layout') ? (
          <SectionCard title="Layout">
            <div className="grid grid-cols-2 gap-2">
              <FieldRow label="Gap (px)">
                <Input
                  className="h-8 text-xs"
                  value={gapPx}
                  onChange={(e) => setGapPx(e.target.value)}
                  onBlur={() => pushCss('gap', `${gapPx}px`, api)}
                />
              </FieldRow>
              <FieldRow label="Display">
                <select
                  className={selectCls}
                  value={display}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDisplay(v);
                    pushCss('display', v, api);
                  }}
                >
                  <option value="flex">flex</option>
                  <option value="inline-flex">inline-flex</option>
                  <option value="grid">grid</option>
                  <option value="block">block</option>
                  <option value="inline-block">inline-block</option>
                </select>
              </FieldRow>
              <FieldRow label="Direction">
                <select
                  className={selectCls}
                  value={flexDir}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFlexDir(v);
                    pushCss('flexDirection', v, api);
                  }}
                >
                  <option value="row">row</option>
                  <option value="column">column</option>
                  <option value="row-reverse">row-reverse</option>
                  <option value="column-reverse">column-reverse</option>
                </select>
              </FieldRow>
              <FieldRow label="Justify">
                <select
                  className={selectCls}
                  value={justify}
                  onChange={(e) => {
                    const v = e.target.value;
                    setJustify(v);
                    pushCss('justifyContent', v, api);
                  }}
                >
                  <option value="flex-start">flex-start</option>
                  <option value="center">center</option>
                  <option value="flex-end">flex-end</option>
                  <option value="space-between">space-between</option>
                  <option value="space-around">space-around</option>
                </select>
              </FieldRow>
              <FieldRow label="Align">
                <select
                  className={selectCls}
                  value={alignItemsSt}
                  onChange={(e) => {
                    const v = e.target.value;
                    setAlignItemsSt(v);
                    pushCss('alignItems', v, api);
                  }}
                >
                  <option value="flex-start">flex-start</option>
                  <option value="center">center</option>
                  <option value="flex-end">flex-end</option>
                  <option value="stretch">stretch</option>
                  <option value="baseline">baseline</option>
                </select>
              </FieldRow>
            </div>
          </SectionCard>
        ) : null}

        {selection.tagName === 'svg' ? (
          <SectionCard title="Icon color">
            <FieldRow label="Fill">
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  className="h-8 w-10 rounded border"
                  value={svgFillHex.length === 7 ? svgFillHex : '#000000'}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSvgFillHex(v);
                    pushCss('fill', v, api);
                  }}
                />
              </div>
            </FieldRow>
            <FieldRow label="Stroke">
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  className="h-8 w-10 rounded border"
                  value={svgStrokeHex.length === 7 ? svgStrokeHex : '#000000'}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSvgStrokeHex(v);
                    pushCss('stroke', v, api);
                  }}
                />
              </div>
            </FieldRow>
          </SectionCard>
        ) : null}

        {has('spacing') ? (
          <SectionCard title="Spacing">
            <BoxMetricRow
              title="Padding"
              defaultExpanded
              top={paddingT}
              right={paddingR}
              bottom={paddingB}
              left={paddingL}
              onTopChange={setPaddingT}
              onRightChange={setPaddingR}
              onBottomChange={setPaddingB}
              onLeftChange={setPaddingL}
              onShorthandBlur={(px) =>
                applyBoxShorthand('padding', px, api, {
                  setT: setPaddingT,
                  setR: setPaddingR,
                  setB: setPaddingB,
                  setL: setPaddingL,
                })
              }
              onSideBlur={(side, px) => pushCss(`padding${side}`, `${px}px`, api)}
            />
            <BoxMetricRow
              title="Margin"
              top={marginT}
              right={marginR}
              bottom={marginB}
              left={marginL}
              onTopChange={setMarginT}
              onRightChange={setMarginR}
              onBottomChange={setMarginB}
              onLeftChange={setMarginL}
              onShorthandBlur={(px) =>
                applyBoxShorthand('margin', px, api, {
                  setT: setMarginT,
                  setR: setMarginR,
                  setB: setMarginB,
                  setL: setMarginL,
                })
              }
              onSideBlur={(side, px) => pushCss(`margin${side}`, `${px}px`, api)}
            />
          </SectionCard>
        ) : null}

        {has('border') ? (
          <SectionCard title="Border">
            <div className="grid grid-cols-2 gap-2">
              <UnitInputRow label="Top" unit="px" value={borderTw} onChange={setBorderTw} onBlur={() => pushCss('borderTopWidth', `${borderTw}px`, api)} />
              <UnitInputRow label="Right" unit="px" value={borderRw} onChange={setBorderRw} onBlur={() => pushCss('borderRightWidth', `${borderRw}px`, api)} />
              <UnitInputRow label="Bottom" unit="px" value={borderBw} onChange={setBorderBw} onBlur={() => pushCss('borderBottomWidth', `${borderBw}px`, api)} />
              <UnitInputRow label="Left" unit="px" value={borderLw} onChange={setBorderLw} onBlur={() => pushCss('borderLeftWidth', `${borderLw}px`, api)} />
            </div>
            <FieldRow label="Style">
              <select
                className={selectCls}
                value={borderStyleState}
                onChange={(e) => {
                  const v = e.target.value;
                  setBorderStyleState(v);
                  pushCss('borderStyle', v, api);
                }}
              >
                {['solid', 'dashed', 'dotted', 'none'].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </FieldRow>
            <FieldRow label="Color">
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  aria-label="Border color"
                  className="h-8 w-10 rounded border border-border bg-background cursor-pointer"
                  value={borderColor.length === 7 ? borderColor : '#000000'}
                  onChange={(e) => {
                    const v = e.target.value;
                    setBorderColor(v);
                    pushCss('borderTopColor', v, api);
                  }}
                />
                <Input
                  className="h-8 text-xs flex-1 font-mono"
                  value={borderColor}
                  onChange={(e) => setBorderColor(e.target.value)}
                  onBlur={() => pushCss('borderTopColor', borderColor, api)}
                />
              </div>
            </FieldRow>
          </SectionCard>
        ) : null}

        {has('radius') ? (
          <SectionCard title="Radius">
            <FieldRow label="Radius (px)">
              <Input className="h-8 text-xs" value={radius} onChange={(e) => setRadius(e.target.value)} onBlur={() => pushCss('borderRadius', `${radius}px`, api)} />
            </FieldRow>
          </SectionCard>
        ) : null}

        {has('box') ? (
          <SectionCard title="Box">
            <FieldRow label="Opacity">
              <Input
                className="h-8 text-xs"
                value={opacity}
                onChange={(e) => setOpacity(e.target.value)}
                onBlur={() => pushCss('opacity', opacity, api)}
              />
            </FieldRow>
            {selection.tagName !== 'svg' ? (
              <FieldRow label="Fill">
                <div className="space-y-1.5">
                  <select
                    key={`${selectionId}-semantic-fill`}
                    className={selectCls}
                    title="Semantic backgrounds"
                    defaultValue=""
                    onChange={(e) => {
                      const opt = SEMANTIC_FILLS.find((x) => x.label === e.target.value);
                      if (!opt) return;
                      setFillHex(rgbToHexApprox(opt.approxCss));
                      pushSemanticBg(opt.util, opt.approxCss, api);
                    }}
                  >
                    <option value="" disabled>
                      Token fill…
                    </option>
                    {SEMANTIC_FILLS.map((o) => (
                      <option key={o.util} value={o.label}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <div className="flex gap-2 items-center">
                    <input
                      type="color"
                      className="h-8 w-10 rounded border"
                      aria-label="Background color"
                      value={fillHex.length === 7 ? fillHex : '#ffffff'}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFillHex(v);
                        pushCss('backgroundColor', v, api);
                      }}
                    />
                    <Input
                      className="h-8 text-xs flex-1 font-mono"
                      value={fillHex}
                      onChange={(e) => setFillHex(e.target.value)}
                      onBlur={() => pushCss('backgroundColor', fillHex, api)}
                    />
                  </div>
                </div>
              </FieldRow>
            ) : null}
          </SectionCard>
        ) : null}
      </div>
    </div>
  );
});

HotEditInner.displayName = 'HotEditInner';

export function HotEditPanel({
  className,
  showCloseInHeader = true,
}: {
  className?: string;
  showCloseInHeader?: boolean;
}) {
  const mode = useStore(hotEditStore.mode);
  const selection = useStore(hotEditStore.selection);
  const api = useStore(hotEditStore.bridgeApi);

  if (!mode || !selection) return null;

  return (
    <aside
      className={cn(
        'flex flex-col min-h-0 rounded-lg border border-border/60 bg-card shadow-sm overflow-hidden',
        className,
      )}
      aria-label="Hot edit inspector"
    >
      {showCloseInHeader ? (
        <div className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-border/60 bg-card shrink-0">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-foreground">
            Hot edit
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-[11px]"
            onClick={() => api?.exitHotEditMode()}
          >
            Close
          </Button>
        </div>
      ) : null}
      <HotEditInner key={selection.elementId} selectionId={selection.elementId} />
    </aside>
  );
}
